/**
 * Darts-Cricket (app/LagKing/DARTS_CRICKET.md, Cole's spec 2026-10-20).
 *
 * The board is the putt line. Each number is a band 3 ft deep:
 *
 *   15  20-23 ft     18  35-38 ft
 *   16  25-28 ft     19  40-43 ft
 *   17  30-33 ft     20  45-48 ft
 *
 * and the bull is a real circle on the line at 55 ft (3 ft outer bull, 1.5 ft
 * red inner bull). The 2 ft gaps between bands, anything short of 20 ft, and
 * anything past 48 ft that is not the bull are misses. The multiplier comes
 * from the start line and fans out like a dartboard wedge: triple inside
 * 0.3 degrees, double inside 0.8, single beyond, with no lateral limit.
 *
 * Standard cricket scoring, 2 to 4 players, 3 putts ("darts") a turn, and an
 * optional round cap. A pure deterministic reducer: state = fold(hits), so
 * local play, the computer and live play (src/liveMatch, built separately)
 * all share it. The engine never sees a raw putt; `readCricketPutt` turns a
 * putt's rolled distance and start line into a hit first. Nothing is random.
 */

/** The seven targets in board order (the helper table's rows). 25 is the bull. */
export const CRICKET_TARGETS = [15, 16, 17, 18, 19, 20, 25] as const;
export type CricketTarget = (typeof CRICKET_TARGETS)[number];
export const CRICKET_BULL: CricketTarget = 25;
export const CRICKET_TARGET_COUNT = CRICKET_TARGETS.length;

/** A number's band down the putt line. Both edges count (20.0 and 23.0 are a 15). */
export interface CricketBand {
  target: Exclude<CricketTarget, 25>;
  minFt: number;
  maxFt: number;
}

export const CRICKET_BANDS: readonly CricketBand[] = [
  { target: 15, minFt: 20, maxFt: 23 },
  { target: 16, minFt: 25, maxFt: 28 },
  { target: 17, minFt: 30, maxFt: 33 },
  { target: 18, minFt: 35, maxFt: 38 },
  { target: 19, minFt: 40, maxFt: 43 },
  { target: 20, minFt: 45, maxFt: 48 },
];

/** |start line| at or inside this is a triple. */
export const CRICKET_TRIPLE_DEG = 0.3;
/** |start line| at or inside this (outside the triple) is a double. */
export const CRICKET_DOUBLE_DEG = 0.8;
/** The bull: centered on the putt line. Radii are half the diameters (3 ft and 1.5 ft). */
export const CRICKET_BULL_CENTER_FT = 55;
export const CRICKET_BULL_OUTER_RADIUS_FT = 1.5;
export const CRICKET_BULL_INNER_RADIUS_FT = 0.75;
/** Points per scoring mark on the bull. */
export const CRICKET_BULL_POINTS = 25;
/** Marks that open (close, for you) a number. */
export const CRICKET_OPEN_MARKS = 3;
export const CRICKET_DARTS_PER_TURN = 3;
export const CRICKET_MIN_PLAYERS = 2;
export const CRICKET_MAX_PLAYERS = 4;

export type CricketLength = 'unlimited' | 'standard' | 'quick';
export const CRICKET_LENGTHS: readonly CricketLength[] = ['unlimited', 'standard', 'quick'];
/** Rounds in a game; null is no limit. */
export const CRICKET_ROUNDS: Record<CricketLength, number | null> = {
  unlimited: null,
  standard: 15,
  quick: 8,
};

export type CricketMultiplier = 1 | 2 | 3;

export type CricketHit =
  | { kind: 'miss' }
  | { kind: 'number'; target: Exclude<CricketTarget, 25>; multiplier: CricketMultiplier }
  /** Outer bull is 1 mark, the inner bull 2. */
  | { kind: 'bull'; marks: 1 | 2 };

/** A putt as the engine reads it: how far it rolled and where it started. */
export interface CricketPuttRead {
  distanceFt: number;
  /** Signed start line, degrees. Null: the gate measured no line. */
  angleDeg: number | null;
}

/** The multiplier for a start line. A missing line reads as a single (never a free triple). */
export function cricketMultiplierFor(angleDeg: number | null): CricketMultiplier {
  if (angleDeg === null || !Number.isFinite(angleDeg)) return 1;
  const a = Math.abs(angleDeg);
  if (a <= CRICKET_TRIPLE_DEG) return 3;
  if (a <= CRICKET_DOUBLE_DEG) return 2;
  return 1;
}

/** The band a rolled distance falls in, or null for a dead zone. */
export function cricketBandFor(distanceFt: number): CricketBand | null {
  if (!Number.isFinite(distanceFt)) return null;
  return CRICKET_BANDS.find((band) => distanceFt >= band.minFt && distanceFt <= band.maxFt) ?? null;
}

/**
 * Where the ball rests relative to the bull's center (ft), on the
 * straight-roll projection the scene draws: lateral = distance x tan(line).
 */
export function cricketBullOffsetFt(distanceFt: number, angleDeg: number): number {
  const lateral = distanceFt * Math.tan((angleDeg * Math.PI) / 180);
  return Math.hypot(lateral, distanceFt - CRICKET_BULL_CENTER_FT);
}

/**
 * A putt's hit. Number bands are judged on the rolled distance with the
 * start-line multiplier; the bull on the resting spot. A putt with no
 * measured line cannot be placed laterally, so it is never a bull.
 */
export function readCricketPutt(read: CricketPuttRead): CricketHit {
  const { distanceFt, angleDeg } = read;
  if (!Number.isFinite(distanceFt)) return { kind: 'miss' };
  const band = cricketBandFor(distanceFt);
  if (band) return { kind: 'number', target: band.target, multiplier: cricketMultiplierFor(angleDeg) };
  if (angleDeg === null || !Number.isFinite(angleDeg)) return { kind: 'miss' };
  const offset = cricketBullOffsetFt(distanceFt, angleDeg);
  if (offset <= CRICKET_BULL_INNER_RADIUS_FT) return { kind: 'bull', marks: 2 };
  if (offset <= CRICKET_BULL_OUTER_RADIUS_FT) return { kind: 'bull', marks: 1 };
  return { kind: 'miss' };
}

export function cricketTargetIndex(target: CricketTarget): number {
  return CRICKET_TARGETS.indexOf(target);
}

/** Points per scoring mark. */
export function cricketTargetValue(target: CricketTarget): number {
  return target === CRICKET_BULL ? CRICKET_BULL_POINTS : target;
}

/** The target a hit lands on, or null for a miss. */
export function cricketHitTarget(hit: CricketHit): CricketTarget | null {
  return hit.kind === 'number' ? hit.target : hit.kind === 'bull' ? CRICKET_BULL : null;
}

export function cricketHitMarks(hit: CricketHit): number {
  return hit.kind === 'number' ? hit.multiplier : hit.kind === 'bull' ? hit.marks : 0;
}

/** One putt's effect, for the popup and the turn summary. */
export interface CricketDart {
  player: number;
  hit: CricketHit;
  /** Marks that counted: toward opening, plus marks that scored. */
  marks: number;
  points: number;
  /** This dart opened the number for the player. */
  opened: boolean;
  /** This dart closed the number for everyone. */
  closedOut: boolean;
  /** The number was already closed for everyone: the hit did nothing. */
  dead: boolean;
}

export type CricketEndReason = 'closed' | 'rounds' | 'clinched';

export interface CricketState {
  players: number;
  length: CricketLength;
  /** Rounds in the game; null is no limit. */
  rounds: number | null;
  /** marks[player][targetIndex], 0 to 3 (extra marks score or are lost). */
  marks: number[][];
  points: number[];
  /** Whose turn it is. */
  turn: number;
  /** Putts already taken this turn (0 to 2 while playing). */
  dart: number;
  /** 1-based round. */
  round: number;
  /** This turn's darts so far. */
  turnDarts: CricketDart[];
  /** The last finished turn (for "THIS TURN: 5 MARKS · 54 PTS"). */
  lastTurn: { player: number; marks: number; points: number; darts: CricketDart[] } | null;
  phase: 'play' | 'complete';
  winner: number | null;
  /** Tied for the most points and marks when the rounds ran out. */
  draw: boolean;
  endedBy: CricketEndReason | null;
  /** Every putt taken, in order. */
  dartCount: number;
  /**
   * Players who stopped mid-game (live play, LIVE_SEATS_PLAN.md): skipped in
   * the turn order, no longer an opponent for scoring or closing, and never
   * the winner. Absent: everyone is in.
   */
  out?: boolean[];
}

/** True while a player is still in the game. */
export function cricketPlayerIn(state: Pick<CricketState, 'out'>, player: number): boolean {
  return state.out?.[player] !== true;
}

/** The next player still in after `from`, in turn order. */
function nextPlayerIn(state: CricketState, from: number): number {
  for (let k = 1; k <= state.players; k += 1) {
    const candidate = (from + k) % state.players;
    if (cricketPlayerIn(state, candidate)) return candidate;
  }
  return from;
}

export interface AppliedCricket {
  state: CricketState;
  /** Null when the putt was ignored (the game is over). */
  dart: CricketDart | null;
  /** The putt finished the turn. */
  turnEnded: boolean;
}

export function createCricketGame(options: { players: number; length?: CricketLength }): CricketState {
  const players = Math.round(options.players);
  if (!(players >= CRICKET_MIN_PLAYERS && players <= CRICKET_MAX_PLAYERS)) {
    throw new Error(`Cricket needs ${CRICKET_MIN_PLAYERS} to ${CRICKET_MAX_PLAYERS} players`);
  }
  const length = options.length ?? 'standard';
  return {
    players,
    length,
    rounds: CRICKET_ROUNDS[length],
    marks: Array.from({ length: players }, () => Array<number>(CRICKET_TARGET_COUNT).fill(0)),
    points: Array<number>(players).fill(0),
    turn: 0,
    dart: 0,
    round: 1,
    turnDarts: [],
    lastTurn: null,
    phase: 'play',
    winner: null,
    draw: false,
    endedBy: null,
    dartCount: 0,
  };
}

/** True once a player has 3 marks on the target. */
export function cricketIsOpen(state: CricketState, player: number, target: CricketTarget): boolean {
  return (state.marks[player]?.[cricketTargetIndex(target)] ?? 0) >= CRICKET_OPEN_MARKS;
}

/** Closed for everyone: every player has 3 marks. */
export function cricketIsClosed(state: CricketState, target: CricketTarget): boolean {
  const index = cricketTargetIndex(target);
  return state.marks.every((row, player) => !cricketPlayerIn(state, player) || row[index] >= CRICKET_OPEN_MARKS);
}

/** Some opponent of `player` still lacks 3 marks: extra marks score. */
export function cricketCanScore(state: CricketState, player: number, target: CricketTarget): boolean {
  const index = cricketTargetIndex(target);
  return state.marks.some((row, other) => other !== player && cricketPlayerIn(state, other) && row[index] < CRICKET_OPEN_MARKS);
}

/** The player has 3 marks on all seven targets. */
export function cricketClosedAll(state: CricketState, player: number): boolean {
  return (state.marks[player] ?? []).every((count) => count >= CRICKET_OPEN_MARKS);
}

/** A player's marks toward closing, 0 to 21 (the round-cap tiebreak). */
export function cricketTotalMarks(state: CricketState, player: number): number {
  return (state.marks[player] ?? []).reduce((sum, count) => sum + Math.min(CRICKET_OPEN_MARKS, count), 0);
}

function closeOutWinner(state: CricketState, player: number): boolean {
  if (!cricketClosedAll(state, player)) return false;
  const mine = state.points[player];
  return state.points.every((points, other) => other === player || !cricketPlayerIn(state, other) || mine >= points);
}

/** Most points, then most marks. Null with `draw` when the best are tied on both. */
export function cricketLeaderByScore(state: CricketState): { winner: number | null; draw: boolean } {
  let best: number[] = [];
  for (let player = 0; player < state.players; player += 1) {
    if (!cricketPlayerIn(state, player)) continue;
    if (best.length === 0) {
      best = [player];
      continue;
    }
    const lead = best[0];
    const byPoints = state.points[player] - state.points[lead];
    const byMarks = cricketTotalMarks(state, player) - cricketTotalMarks(state, lead);
    if (byPoints > 0 || (byPoints === 0 && byMarks > 0)) best = [player];
    else if (byPoints === 0 && byMarks === 0) best.push(player);
  }
  return best.length === 1 ? { winner: best[0], draw: false } : { winner: null, draw: true };
}

/** Apply one putt's hit for the player whose turn it is. Never mutates `state`. */
export function applyCricketHit(state: CricketState, hit: CricketHit): AppliedCricket {
  if (state.phase === 'complete') return { state, dart: null, turnEnded: false };
  const player = state.turn;
  const marks = state.marks.map((row) => [...row]);
  const points = [...state.points];
  const target = cricketHitTarget(hit);
  const thrown = cricketHitMarks(hit);
  let counted = 0;
  let scored = 0;
  let opened = false;
  let closedOut = false;
  let dead = false;
  if (target !== null && thrown > 0) {
    const index = cricketTargetIndex(target);
    if (cricketIsClosed(state, target)) {
      dead = true;
    } else {
      const before = marks[player][index];
      const toOpen = Math.max(0, CRICKET_OPEN_MARKS - before);
      const opening = Math.min(toOpen, thrown);
      const extra = thrown - opening;
      marks[player][index] = Math.min(CRICKET_OPEN_MARKS, before + thrown);
      counted = opening;
      opened = before < CRICKET_OPEN_MARKS && marks[player][index] >= CRICKET_OPEN_MARKS;
      // Extra marks score only while some opponent has not closed it (the
      // state before this dart; this dart cannot close an opponent).
      if (extra > 0 && cricketCanScore(state, player, target)) {
        scored = extra * cricketTargetValue(target);
        counted += extra;
        points[player] += scored;
      }
      closedOut = marks.every((row, other) => !cricketPlayerIn(state, other) || row[index] >= CRICKET_OPEN_MARKS);
    }
  }
  const dart: CricketDart = { player, hit, marks: counted, points: scored, opened, closedOut, dead };
  const turnDarts = [...state.turnDarts, dart];
  let next: CricketState = {
    ...state,
    marks,
    points,
    dart: state.dart + 1,
    turnDarts,
    dartCount: state.dartCount + 1,
  };
  // The thrower closes everything with points >= everyone: game over now.
  if (closeOutWinner(next, player)) {
    return {
      state: {
        ...next,
        phase: 'complete',
        winner: player,
        endedBy: 'closed',
        lastTurn: summarizeTurn(player, turnDarts),
        turnDarts: [],
      },
      dart,
      turnEnded: true,
    };
  }
  // Every number closed for everyone and still no close-out winner (only
  // possible with 3 or 4 players): nothing can change any more, so the most
  // points win now.
  if (CRICKET_TARGETS.every((each) => cricketIsClosed(next, each))) {
    const result = cricketLeaderByScore(next);
    return {
      state: {
        ...next,
        phase: 'complete',
        winner: result.winner,
        draw: result.draw,
        endedBy: 'closed',
        lastTurn: summarizeTurn(player, turnDarts),
        turnDarts: [],
      },
      dart,
      turnEnded: true,
    };
  }
  if (next.dart < CRICKET_DARTS_PER_TURN) return { state: next, dart, turnEnded: false };
  // Turn over.
  const lastTurn = summarizeTurn(player, turnDarts);
  // The next player still in; a new round begins when the turn wraps past the end.
  const nextTurn = nextPlayerIn(state, player);
  const nextRound = nextTurn <= player ? state.round + 1 : state.round;
  next = { ...next, turn: nextTurn, dart: 0, round: nextRound, turnDarts: [], lastTurn };
  if (state.rounds !== null && nextRound > state.rounds) {
    const result = cricketLeaderByScore(next);
    next = {
      ...next,
      turn: player,
      round: state.rounds,
      phase: 'complete',
      winner: result.winner,
      draw: result.draw,
      endedBy: 'rounds',
    };
  }
  return { state: next, dart, turnEnded: true };
}

function summarizeTurn(player: number, darts: readonly CricketDart[]): NonNullable<CricketState['lastTurn']> {
  return {
    player,
    marks: darts.reduce((sum, dart) => sum + dart.marks, 0),
    points: darts.reduce((sum, dart) => sum + dart.points, 0),
    darts: [...darts],
  };
}

/** Read and apply a putt in one step. */
export function applyCricketPutt(state: CricketState, read: CricketPuttRead): AppliedCricket {
  return applyCricketHit(state, readCricketPutt(read));
}

/** Fold a list of hits from a fresh game (the live layer folds the same way). */
export function foldCricket(
  options: { players: number; length?: CricketLength },
  hits: readonly CricketHit[],
): CricketState {
  let state = createCricketGame(options);
  for (const hit of hits) state = applyCricketHit(state, hit).state;
  return state;
}

/** Putts a player has left in a capped game from here (the current turn's remainder included). */
export function cricketDartsLeft(state: CricketState, player: number): number | null {
  if (state.rounds === null) return null;
  if (state.phase === 'complete') return 0;
  const fullRoundsAfter = Math.max(0, state.rounds - state.round);
  let thisRound: number;
  if (player > state.turn) thisRound = CRICKET_DARTS_PER_TURN;
  else if (player === state.turn) thisRound = CRICKET_DARTS_PER_TURN - state.dart;
  else thisRound = 0;
  return thisRound + fullRoundsAfter * CRICKET_DARTS_PER_TURN;
}

/**
 * The most points `player` could still add with one putt: three marks on the
 * best number it can still score on (the bull counts two marks of 25). A
 * number scores for it only while some opponent has not closed it.
 */
export function cricketBestDartPoints(state: CricketState, player: number): number {
  let best = 0;
  for (const target of CRICKET_TARGETS) {
    if (!cricketCanScore(state, player, target)) continue;
    const value = target === CRICKET_BULL ? 2 * CRICKET_BULL_POINTS : 3 * target;
    if (value > best) best = value;
  }
  return best;
}

export interface CricketClinch {
  leader: number;
  /** Every other player, none of whom can win any more. */
  trailers: number[];
  /** The trailer closest to catching up, with what it needs and its best possible gain. */
  closest: { player: number; needs: number; bestPossible: number };
}

/**
 * Mathematically decided, conservatively (DARTS_CRICKET.md "Clinch"). Only in
 * a capped game: the leader's points never go down, so a trailer whose points
 * plus the most it could add with every putt it has left (60 a putt at most,
 * a triple 20) still fall short of the leader's points now can never match
 * them: it cannot win at the round cap (fewer points) or by closing out
 * (closing out needs points >= everyone). When that holds for every other
 * player the leader wins every continuation. Shown only while some trailer
 * still has a putt to take; with none left the game ends by itself.
 */
export function cricketClinch(state: CricketState): CricketClinch | null {
  if (state.phase !== 'play' || state.rounds === null) return null;
  let leader = -1;
  for (let player = 0; player < state.players; player += 1) {
    if (!cricketPlayerIn(state, player)) continue;
    if (leader < 0 || state.points[player] > state.points[leader]) leader = player;
  }
  if (leader < 0) return null;
  const leaderPoints = state.points[leader];
  const trailers: number[] = [];
  let closest: CricketClinch['closest'] | null = null;
  let anyDartsLeft = false;
  for (let player = 0; player < state.players; player += 1) {
    if (player === leader || !cricketPlayerIn(state, player)) continue;
    const darts = cricketDartsLeft(state, player) ?? 0;
    if (darts > 0) anyDartsLeft = true;
    const bestPossible = darts * cricketBestDartPoints(state, player);
    const needs = leaderPoints - state.points[player];
    if (state.points[player] + bestPossible >= leaderPoints) return null;
    trailers.push(player);
    if (!closest || needs - bestPossible < closest.needs - closest.bestPossible) {
      closest = { player, needs, bestPossible };
    }
  }
  if (!anyDartsLeft || !closest) return null;
  return { leader, trailers, closest };
}

/** END GAME on a clinched game: a normal win for the leader at the score as it stands. */
export function endCricketGameClinched(state: CricketState): { state: CricketState } {
  const clinch = cricketClinch(state);
  if (!clinch) return { state };
  return {
    state: {
      ...state,
      phase: 'complete',
      winner: clinch.leader,
      draw: false,
      endedBy: 'clinched',
      turnDarts: [],
    },
  };
}

/**
 * A player stops mid-game (live play, LIVE_SEATS_PLAN.md). They are out from
 * here: on their own turn the turn ends now (a partial turn is summarized as
 * any other), and the game ends at once if what is left decides it: one
 * player still in, every number closed among those still in, a close-out
 * already made, or the round cap passed. Never mutates `state`.
 */
export function removeCricketPlayer(state: CricketState, player: number): CricketState {
  if (state.phase === 'complete' || !cricketPlayerIn(state, player) || player < 0 || player >= state.players) return state;
  const out = Array.from({ length: state.players }, (_, index) => !cricketPlayerIn(state, index) || index === player);
  let next: CricketState = { ...state, out };
  const stillIn = out.flatMap((isOut, index) => (isOut ? [] : [index]));
  if (state.turn === player) {
    const nextTurn = nextPlayerIn(next, player);
    const nextRound = nextTurn <= player ? state.round + 1 : state.round;
    next = {
      ...next,
      turn: nextTurn,
      dart: 0,
      round: nextRound,
      turnDarts: [],
      lastTurn: state.turnDarts.length > 0 ? summarizeTurn(player, state.turnDarts) : state.lastTurn,
    };
    if (state.rounds !== null && nextRound > state.rounds) {
      const result = cricketLeaderByScore(next);
      return { ...next, round: state.rounds, phase: 'complete', winner: result.winner, draw: result.draw, endedBy: 'rounds' };
    }
  }
  if (stillIn.length === 1) {
    return { ...next, phase: 'complete', winner: stillIn[0], draw: false, endedBy: 'closed', turnDarts: [] };
  }
  const closer = stillIn.find((index) => closeOutWinner(next, index));
  if (closer !== undefined) {
    return { ...next, phase: 'complete', winner: closer, draw: false, endedBy: 'closed', turnDarts: [] };
  }
  if (CRICKET_TARGETS.every((each) => cricketIsClosed(next, each))) {
    const result = cricketLeaderByScore(next);
    return { ...next, phase: 'complete', winner: result.winner, draw: result.draw, endedBy: 'closed', turnDarts: [] };
  }
  return next;
}
